import React, { ReactNode, FC } from 'react';
import { useHistory } from 'app/components/router/History';
import { History } from 'app/services/history';
import { useQuery } from 'app/components/Contexts';
import { LoginRoute } from 'app/constants/Routes';
import { getAuthUser } from 'app/models/auth/selectors';
import { connect } from 'react-redux';
import { User } from 'app/constants/schemas/User';
import { getDestinationRouteForUser } from 'app/components/auth/UserSensitiveLink';
import { DMMAuthService } from 'app/services/oauth/DMMAuthService';
import { finishDMMLogin } from 'app/services/oauth/login';
import RedirectingPage from '../pages/RedirectingPage';
import { warn } from 'app/services/logger';
import { stringify } from 'query-string';
import { ContinuationMissingError } from 'app/services/oauth/Continuation';
import { notify } from 'app/services/errorReporter';
import { useBrand, BrandContextValue } from '../OrganizationContext';
import { getBrandDomainLocation } from 'app/utils/brand';

interface IProps {
  children: (errorResult: { error: unknown }) => ReactNode;
  history: History;
  query: { [k: string]: string };
  brand: BrandContextValue;
  user: User;
}

class DMMLoginCallback extends React.Component<IProps> {
  public componentDidMount() {
    void this.onContinue();
  }

  public render() {
    return <RedirectingPage />;
  }

  private async onContinue() {
    const { history, query, brand, user } = this.props;
    const dmmAuthService = new DMMAuthService(history);

    try {
      await finishDMMLogin(query, { dmmAuthService });
    } catch (error) {
      // We should expected ContinuationMissingError errors, since
      // they are caused by links embedded in signup emails that have
      // a non trivial lifetime. They also are passed externally to
      // the current flow, so could land up in an unrelated browser or
      // even an unrelated computer.

      warn('DMMLoginCallback error', { error });

      if (!(error instanceof ContinuationMissingError)) {
        notify(error, { name: 'DMMLoginCallback.onContinue' });
      }

      if (user) {
        // If they're already authenticated, they probably won't be
        // confused by the main dashboard, or the default redirect for
        // their brand.
        const defaultBrandLocation = getBrandDomainLocation(brand.defaultDomain);
        history.smartReplace(getDestinationRouteForUser(user, brand, defaultBrandLocation));
      } else {
        // If they're not authenticated, they don't really want to see
        // a "login button".  We know you're coming back from DMM as
        // part of logging in. Try to continue.
        history.replace({
          pathname: LoginRoute.getPathname(),
          search:   stringify({ automatic: true }),
        });
      }
    }
  }
}

const ConnectedOauthAttachCallback = connect(
  (state) => ({
    user: getAuthUser(state),
  }),
)(DMMLoginCallback);

const DMMLoginCallbackInjectDependencies: FC<Pick<IProps, 'children'>> = (props) => {
  const history = useHistory();
  const query = useQuery();
  const brand = useBrand();
  return (
    <ConnectedOauthAttachCallback
      history={history}
      query={query}
      brand={brand}
      {...props}
    />
  );
};

export default DMMLoginCallbackInjectDependencies;
